import React, { useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { tablePendingJobs } from "../../tableWorkload";
import "./TableWiseView.css";

const FILTERS = [
  { value: "active", label: "Active" },
  { value: "all", label: "All tables" },
];

const number = (value) =>
  new Intl.NumberFormat().format(Number.isFinite(Number(value)) ? Number(value) : 0);

const getActiveOrderCount = (jobs) => {
  const ids = jobs.map((job, index) => job?.orderId ?? job?.order_id ?? job?.id ?? `job-${index}`);
  return new Set(ids).size;
};

const getWorkloadTotals = (tables) =>
  tables.reduce(
    (result, table) => ({
      pending: result.pending + (Number(table?.pendingQty) || 0),
      processing: result.processing + (Number(table?.processingQty) || 0),
      orders: result.orders + getActiveOrderCount(Array.isArray(table?.jobs) ? table.jobs : []),
    }),
    { pending: 0, processing: 0, orders: 0 }
  );

export const TableWorkloadSummary = ({ tables = [], loading = false, error = "" }) => {
  const safeTables = Array.isArray(tables) ? tables : [];
  const totals = getWorkloadTotals(safeTables);
  const unavailable = Boolean(error) || (loading && safeTables.length === 0);
  const metric = (value) => (unavailable ? "—" : number(value));

  return (
    <div className="kds-table-workload" role="group" aria-label="Current table workload">
      <span className="kds-table-workload__label">Current table workload</span>
      <div className="kds-table-workload__metrics">
        <span className="kds-table-workload__metric kds-table-workload__metric--orders">
          <strong>{metric(totals.orders)}</strong><span>active orders</span>
        </span>
        <span className="kds-table-workload__metric kds-table-workload__metric--prepare">
          <strong>{metric(totals.pending)}</strong><span>to prepare</span>
        </span>
        <span className="kds-table-workload__metric kds-table-workload__metric--processing">
          <strong>{metric(totals.processing)}</strong><span>processing</span>
        </span>
      </div>
    </div>
  );
};

const displayNames = (names) => {
  if (!Array.isArray(names)) return "";
  return names.filter((name) => typeof name === "string" && name.trim()).map((name) => name.trim()).join(", ");
};

const TableWiseView = ({
  tables = [],
  loading = false,
  error = "",
  onRetry,
  onOpenTable,
  onAcceptOrders,
  onRejectOrders,
  actionBusy = false,
  activeAction = null,
  allowAccept = true,
  allowReject = true,
  filter: controlledFilter,
  onFilterChange,
}) => {
  const [localFilter, setLocalFilter] = useState("active");
  // The KDS page controls the filter so it can load the full table catalogue only for "All tables".
  const filter = controlledFilter ?? localFilter;
  const setFilter = onFilterChange ?? setLocalFilter;
  const invokeDecision = async (handler, ids, table) => {
    try { await handler?.(ids, table); }
    catch (_) { /* The action reports its error; keep both sync and async failures contained. */ }
  };
  const safeTables = Array.isArray(tables) ? tables : [];
  const visibleTables = useMemo(
    () =>
      safeTables.filter((table) => {
        const active = getActiveOrderCount(Array.isArray(table?.jobs) ? table.jobs : []) > 0;
        if (filter === "active") return active;
        return true;
      }),
    [safeTables, filter]
  );

  return (
    <section className="kds-table-view" aria-label="Kitchen workload by table">
      <div className="kds-table-view__toolbar">
        <span className="kds-table-view__visible-count">
          <strong>{number(visibleTables.length)}</strong> of {number(safeTables.length)} tables
        </span>
        <div className="kds-table-view__filters" role="group" aria-label="Filter tables">
          {FILTERS.map((option) => (
            <button
              type="button"
              key={option.value}
              aria-pressed={filter === option.value}
              className={filter === option.value ? "is-selected" : ""}
              onClick={() => setFilter(option.value)}
            >{option.label}</button>
          ))}
        </div>
      </div>

      {error && (
        <div className="kds-table-view__message kds-table-view__message--error" role="alert">
          <div><strong>Tables couldn’t be loaded</strong><span>{error}</span></div>
          {typeof onRetry === "function" && <button type="button" onClick={onRetry}>Try again</button>}
        </div>
      )}

      {!error && loading && safeTables.length === 0 && (
        <div className="kds-table-view__grid" aria-label="Loading tables" aria-busy="true">
          {[0, 1, 2, 3, 4, 5].map((item) => (
            <div className="kds-table-view__skeleton" key={item}>
              <span /><span /><span />
            </div>
          ))}
        </div>
      )}

      {!error && !loading && safeTables.length === 0 && (
        <div className="kds-table-view__message">
          <span className="kds-table-view__empty-rule" />
          <div><strong>No tables to show</strong><span>Tables will appear here when they are available to the kitchen.</span></div>
        </div>
      )}

      {!error && safeTables.length > 0 && visibleTables.length === 0 && (
        <div className="kds-table-view__message">
          <div><strong>No tables in this view</strong><span>Choose another filter to see the full floor.</span></div>
        </div>
      )}

      {!error && visibleTables.length > 0 && (
        <div className="kds-table-view__grid" aria-busy={loading}>
          {visibleTables.map((table, index) => {
            const jobs = Array.isArray(table?.jobs) ? table.jobs : [];
            const orders = getActiveOrderCount(jobs);
            const pendingJobs = [...new Map(tablePendingJobs(table)
              .map((job) => [job.jobId, job])).values()];
            const pendingIds = pendingJobs.map((job) => job.jobId);
            const active = orders > 0;
            const occupancy = table?.occupancy || {
              kind: "unknown",
              label: "Occupancy unavailable",
              durationLabel: "—",
            };
            const occupancyLabel = occupancy.label || "Occupancy unavailable";
            const occupancyDuration = occupancy.kind === "occupied"
              ? (occupancy.durationLabel && occupancy.durationLabel !== "—" ? occupancy.durationLabel : "Time unavailable")
              : occupancy.kind === "available" ? "—" : (occupancy.durationLabel || "—");
            const tone = ["green", "orange", "red"].includes(table?.timing?.tone)
              ? table.timing.tone
              : "neutral";
            const location = [table?.areaName, table?.floorName].filter(Boolean).join(" · ");
            const captains = displayNames(table?.captainNames) || "Not available";
            const customers = displayNames(table?.customerNames) || "Not available";
            return (
              <article
                className={`kds-table-view__card${filter === "all" ? ` kds-table-view__card--all-tables kds-table-view__card--occupancy-${occupancy.kind}` : ` kds-table-view__card--${tone}${active ? " is-active" : " is-available"}`}`}
                key={table?.key ?? `${table?.name}-${index}`}
              >
                <div className="kds-table-view__card-main">
                  <div className="kds-table-view__card-top">
                    <div>
                      <span className="kds-table-view__table-label">Table</span>
                      <h3 title={table?.name || "Unnamed table"}>{table?.name || "Unnamed table"}</h3>
                    </div>
                    {filter === "all" ? (
                      <span className="kds-table-view__state kds-table-view__occupancy-state">{occupancyLabel}</span>
                    ) : (
                      <span className={`kds-table-view__state${active ? " is-active" : ""}`}>
                        {active ? `${number(orders)} ${orders === 1 ? "order" : "orders"}` : "No kitchen orders"}
                      </span>
                    )}
                  </div>
                  {location && <p className="kds-table-view__location">{location}</p>}
                  {filter === "all" ? (
                    <div className="kds-table-view__occupancy-time">
                      <span>{occupancy.kind === "occupied" ? "Time at table" : "Customer time"}</span>
                      <strong>{occupancyDuration}</strong>
                    </div>
                  ) : (
                    <>
                      <div className="kds-table-view__people">
                        <span><strong>Captain</strong><span title={captains}>{captains}</span></span>
                        <span><strong>Customer</strong><span title={customers}>{customers}</span></span>
                      </div>
                      <div className="kds-table-view__timer">
                        {active ? (
                          <>
                            <div className="kds-table-view__timer-copy">
                              <span>{table?.timing?.label || "Order timing"}</span>
                              <small>{table?.timing?.description || "Current table urgency"}</small>
                            </div>
                            <strong>{table?.timing?.display || "—"}</strong>
                          </>
                        ) : <span>No matching kitchen orders</span>}
                      </div>
                      <div className="kds-table-view__counts">
                        <span><i className="is-pending" />To prepare <strong>{number(table?.pendingQty)}</strong></span>
                        <span><i className="is-processing" />Processing <strong>{number(table?.processingQty)}</strong></span>
                      </div>
                      <div className="kds-table-view__received">
                        <span>First received</span>
                        <strong>{active ? table?.receivedLabel || "—" : "—"}</strong>
                      </div>
                    </>
                  )}
                </div>
                <button type="button" className="kds-table-view__open-button"
                  onClick={() => typeof onOpenTable === "function" && onOpenTable(table?.key)}
                  disabled={typeof onOpenTable !== "function"}
                  aria-label={filter === "all"
                    ? `Open table ${table?.name || "table"}, ${occupancyLabel}, ${occupancyDuration}`
                    : `Open table ${table?.name || "table"}, ${active ? `${orders} active orders` : "no matching kitchen orders"}`}>
                </button>
                {filter === "active" && pendingIds.length > 0 && (
                  <div className="kds-table-view__pending-actions" aria-label={`Pending order actions for table ${table?.name || ""}`}>
                    <span>New orders · {number(pendingIds.length)} {pendingIds.length === 1 ? "order" : "orders"}</span>
                    <div>
                      <button type="button" className="is-reject" disabled={actionBusy || loading || !!error || !allowReject || typeof onRejectOrders !== "function"}
                        onClick={() => invokeDecision(onRejectOrders, pendingIds, table)} aria-label={`Reject new orders for table ${table?.name}, ${pendingIds.length} orders`}>
                        {actionBusy && activeAction?.tableKey === table?.key && activeAction?.status === "REJECTED"
                          ? <><CircularProgress size={12} color="inherit" aria-label="Rejecting table orders" /> Rejecting…</> : "Reject new"}
                      </button>
                      <button type="button" className="is-accept" disabled={actionBusy || loading || !!error || !allowAccept || typeof onAcceptOrders !== "function"}
                        onClick={() => invokeDecision(onAcceptOrders, pendingIds, table)} aria-label={`Accept new orders for table ${table?.name}, ${pendingIds.length} orders`}>
                        {actionBusy && activeAction?.tableKey === table?.key && activeAction?.status === "IN_PROGRESS"
                          ? <><CircularProgress size={12} color="inherit" aria-label="Accepting table orders" /> Accepting…</> : "Accept new"}
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default TableWiseView;
